/**
 * One agent's setup, in the order the job happens.
 *
 * Arun: *"when i clikc on an agent, you will sho me the setup required for each agent under this
 * so its easy to keep trac … think what is account level and waht is agent level and move agent
 * level things to under each agent so its easy to manage."*
 *
 * So this page is the agent level: two cards and a bookkeeping row, top to bottom in the order a
 * callback actually travels:
 *
 *  1. **The post-call hook.** Nothing else here matters until this agent's finished calls arrive
 *     in this app. Four numbered blocks, ending in a brief to hand over — nothing on this page
 *     writes to the tenant itself.
 *  2. **Can it dial back?** This agent's channels, and the overrides for the ones that cannot
 *     place a call. A campaign-dialler channel looks perfectly configured and silently strands
 *     every callback it sends.
 *
 * Two things used to be sections of their own here and are now blocks *inside* the hook card,
 * which is the better place for both. **What the callback knows when it rings back** is block 2:
 * context is a question about this hook's payload, so it belongs beside the hook rather than in
 * a card that could be read as a separate step. **The agent's own disposition values** are block
 * 3, where each value is a control rather than a row of a list — reading a value list is not a
 * task, and deciding what a value *does* is.
 *
 * Then a short administrative card — the note, and taking it off the list — kept last and kept
 * plain, because it is bookkeeping rather than setup.
 *
 * Everything on this page comes from two queries the Agents tab already loaded, so arriving here
 * from that list costs one live 8loop read (the hook plan) and nothing else.
 */

import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { PageHeader } from "@/components/ui/page-header";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { errorMessage, type CallbackAgent } from "@/lib/api";
import { relativeTime } from "@/lib/labels";
import {
  useAccountConfig,
  useCallbackAgents,
  useRemoveCallbackAgent,
} from "@/lib/queries";
import { CodeActionSection } from "@/pages/settings/code-action-section";
import { AgentChannelsSection } from "@/pages/settings/routes-section";

export function AgentPage() {
  const { accountId, agentId } = useParams<{ accountId: string; agentId: string }>();
  const config = useAccountConfig(accountId);
  const agents = useCallbackAgents(accountId);

  const agent = agents.data?.agents.find((a) => a.agent_id === agentId) ?? null;
  const name = agent?.agent_name ?? agents.data?.unlisted.find((u) => u.agent_id === agentId)?.agent_name;

  useEffect(() => {
    document.title = name ? `${name} · Configuration` : "Agent · Configuration";
  }, [name]);

  const backToList = `/accounts/${accountId}/settings?tab=agents`;

  return (
    <AppShell active="settings">
      <div className="flex h-full w-full max-w-full flex-col">
        {/*
          Getting out is top-left, not top-right. Arun: *"can you put the byttin to go back to
          agents on left instead of right - hard to gind and npt obvious."* It replaces
          PageHeader's own breadcrumb row rather than sitting above it — one row, in the place a
          breadcrumb would be, so the header does not grow a third line. The path stays to its
          right as location; the button is the action.
        */}
        <div className="flex items-center gap-2 px-6 pt-3 pb-1 text-xs text-muted-foreground">
          <Button
            variant="outline"
            size="sm"
            asChild
            className="-ml-0.5 h-7 gap-1.5 px-2 text-xs"
          >
            <Link to={backToList}>
              <ArrowLeft className="size-3.5" />
              All agents
            </Link>
          </Button>
          <span className="text-border select-none">/</span>
          <Link to="/accounts" className="transition-colors hover:text-foreground">
            Accounts
          </Link>
          <span className="text-border select-none">/</span>
          <Link to={backToList} className="truncate transition-colors hover:text-foreground">
            {config.data?.account.name ?? "Account"}
          </Link>
          <span className="text-border select-none">/</span>
          <span className="truncate font-medium text-foreground">
            {name ?? `Agent ${agentId}`}
          </span>
        </div>
        <PageHeader
          title={name ?? `Agent ${agentId}`}
          description={
            agent
              ? `Everything this agent needs for its finished calls to become callbacks here. Added ${relativeTime(agent.added_at)}.`
              : "One agent's callback setup."
          }
          className="[&>div]:pt-1"
        />

        <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
          <div className="max-w-3xl space-y-6 pb-10">
            {config.isError ? (
              <ErrorState
                title="Couldn't load this account's configuration"
                description={errorMessage(config.error)}
                onRetry={() => config.refetch()}
              />
            ) : agents.isError ? (
              <ErrorState
                title="Couldn't load this account's agent list"
                description={errorMessage(agents.error)}
                onRetry={() => agents.refetch()}
              />
            ) : config.isPending || agents.isPending ? (
              <div className="space-y-4">
                <Skeleton className="h-56 w-full rounded-xl" />
                <Skeleton className="h-48 w-full rounded-xl" />
              </div>
            ) : !agent ? (
              /*
                A bookmarked page for an agent since removed, or a hand-typed id. Not an error:
                say which of the two it is and point at the list, which is the only place it can
                be put back.
              */
              <EmptyState
                title="This agent is not on the account's callback list"
                description={`Nothing on this account is set up for agent ${agentId}. It may have been taken off the list, or the id may be wrong. Add it from the Agents tab and its setup — the fields it captures, what its own values mean, and any channel override — comes back.`}
                action={
                  <Button asChild>
                    <Link to={backToList}>Go to the agents list</Link>
                  </Button>
                }
              />
            ) : (
              <>
                {/* Stated before the setup, because both change what the rest of the page means:
                    an archived agent will never send another callback, and one 8loop no longer
                    lists may not exist at all. */}
                {(agent.archived || agent.missing_from_tenant) && (
                  <Alert variant={agent.missing_from_tenant ? "destructive" : undefined}>
                    <AlertTriangle className="size-4" />
                    <AlertDescription>
                      {agent.missing_from_tenant
                        ? "8loop's last channel list had no channel for this agent, so it may have been deleted on the tenant. Its setup here is kept, but nothing will arrive from it."
                        : "This agent is archived in 8loop, so it will not take another call and will not send another callback. Its setup here is kept."}
                    </AlertDescription>
                  </Alert>
                )}

                {/* 1. the hook — the only step that writes to the tenant */}
                <CodeActionSection accountId={accountId!} config={config.data} agent={agent} />

                {/* 2. can it dial back */}
                <AgentChannelsSection
                  accountId={accountId!}
                  config={config.data}
                  agentId={agent.agent_id}
                />

                <AgentAdminSection accountId={accountId!} agent={agent} backToList={backToList} />
              </>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/**
 * Taking this agent off the callback list.
 *
 * Arun: *"do we need this - 'Why this agent is on the callback list / Bookkeeping, for whoever
 * opens this page next…' what purpise doe sthis erve or what problem does this solve?"*
 *
 * Mostly it did not. It held an editable note nobody had written, two warnings that belonged to
 * other sections, and a heading that named a record rather than a job. What it is genuinely for
 * is one thing: this is the only place an agent can be taken off the list. So that is all it is
 * now — the identity line, and the way out.
 *
 * Removing stays deliberately undramatic and deliberately explicit about what it does *not* do.
 * It deletes one row in this app. The hook stays installed on the tenant and keeps posting, so
 * the callbacks keep arriving and keep being dialled — the opposite of what "remove" implies,
 * and exactly why the confirmation says it in full.
 */
function AgentAdminSection({
  accountId,
  agent,
  backToList,
}: {
  accountId: string;
  agent: CallbackAgent;
  backToList: string;
}): React.ReactElement {
  const remove = useRemoveCallbackAgent(accountId);
  const navigate = useNavigate();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">
        <span className="font-mono">{agent.agent_id}</span> · on this account's callback list since{" "}
        {relativeTime(agent.added_at)}
        {agent.callbacks_seen > 0 &&
          ` · ${agent.callbacks_seen} callback${agent.callbacks_seen === 1 ? "" : "s"} received`}
      </p>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            disabled={remove.isPending}
          >
            {remove.isPending ? "Removing…" : "Take off the list"}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Take {agent.agent_name ?? `agent ${agent.agent_id}`} off the callback list?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This only removes it from this list. Nothing on the tenant changes.
              {agent.hook.present === true
                ? " The post-call hook stays installed on the agent in 8loop and keeps posting, so its callbacks will keep arriving and keep being dialled. Delete the webhook in 8loop if you want that to stop."
                : agent.hook.present === false
                  ? " Nothing was reaching this app from this agent anyway."
                  : " This agent has never been checked against 8loop, so if a hook is installed on it, its callbacks will keep arriving and keep being dialled."}{" "}
              Anything set up here is kept, so adding it back restores it exactly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() =>
                remove.mutate(agent.agent_id, {
                  onSuccess: (r) => {
                    toast.success(r.message);
                    navigate(backToList);
                  },
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              Take it off the list
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
